package com.eladfinish.bible.contest.screens.search.ui

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.data.common.MyClipboardManager
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.features.share.ShareLauncher
import com.eladfinish.bible.contest.lifecycle.KeepScreenOn
import com.eladfinish.bible.contest.ok
import com.eladfinish.bible.contest.platform.haptics.HapticService
import com.eladfinish.bible.contest.screens.search.SearchStore
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction
import com.eladfinish.bible.contest.screens.search.mvi.SearchEffect
import com.eladfinish.bible.contest.screens.search.ui.menu.SearchMenuSync
import com.eladfinish.bible.contest.screens.search.ui.menu.SearchToolbarBindings
import com.eladfinish.bible.contest.search_history_saved_hint
import com.eladfinish.bible.contest.search_history_show
import com.eladfinish.bible.contest.search_in_bible
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.menus.actions.SearchToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import com.eladfinish.bible.contest.ui.scaffold.ShowSnackbar
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun SearchScreen(
    initialText: String,
    store: SearchStore = koinViewModel(),
    toolbarActionBus: ToolbarActionBusViewModel,
    showToast: (String) -> Unit,
    showToastStringDesc: (StringDesc) -> Unit,
    showSnackbar: ShowSnackbar,
    hapticService: HapticService = getKoinInstance(),
    myClipboardManager: MyClipboardManager = getKoinInstance(),
    shareLauncher: ShareLauncher = getKoinInstance(),
) {
    val state by store.observeState().collectAsState()

    LaunchedEffect(initialText) {
        store.dispatch(SearchAction.Initialize(initialText))
    }

    val signsScrollState = rememberScrollState()

    ObserveSearchSideEffects(
        store = store,
        showToast = showToast,
        showToastStringDesc = showToastStringDesc,
        showSnackbar = showSnackbar,
        hapticService = hapticService,
        myClipboardManager = myClipboardManager,
        shareLauncher = shareLauncher,
        signsScrollState = signsScrollState,
    )

    KeepScreenOn(
        enabled = state.isSearching,
    )

    SearchScreenContent(
        state = state,
        signsScrollState = signsScrollState,
        onAction = store::dispatch,
    )

    SearchToolbarBindings(
        toolbarActionBus = toolbarActionBus,
        onAction = store::dispatch,
    )

    SearchMenuSync(
        state = state,
    )

    SearchDialogs(
        dialogState = state.dialogState,
        onAction = store::dispatch,
    )

    SetScreenIsLoading(
        isLoading = state.isLoading,
    )
}

@Composable
private fun ObserveSearchSideEffects(
    store: SearchStore,
    showToast: (String) -> Unit,
    showToastStringDesc: (StringDesc) -> Unit,
    showSnackbar: ShowSnackbar,
    hapticService: HapticService,
    myClipboardManager: MyClipboardManager,
    shareLauncher: ShareLauncher,
    signsScrollState: ScrollState,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect { effect ->
            when (effect) {
                SearchEffect.ShowKeyboard -> {
                    keyboardController?.show()
                }

                SearchEffect.HideKeyboard -> {
                    keyboardController?.hide()
                }

                SearchEffect.ScrollSignsContainerToStart -> {
                    signsScrollState.animateScrollTo(0)
                }

                is SearchEffect.ShowRawMessage -> {
                    showToast(effect.message)
                }

                is SearchEffect.ShowMessage -> {
                    showToastStringDesc(effect.message)
                }

                is SearchEffect.ShowSearchSuggestion -> {
                    showSnackbar(
                        effect.message,
                        SharedRes.strings.ok.desc(),
                        SnackbarDuration.Short,
                    ) {
                        store.dispatch(
                            SearchAction.SearchSuggestionAccepted(
                                suggestedExpression = effect.suggestedExpression,
                            ),
                        )
                    }
                }

                SearchEffect.ShowHistorySavedHint -> {
                    showSnackbar(
                        SharedRes.strings.search_history_saved_hint.desc(),
                        SharedRes.strings.search_history_show.desc(),
                        SnackbarDuration.Long,
                    ) {
                        store.dispatch(
                            SearchAction.ToolbarClicked(SearchToolbarAction.History),
                        )
                    }
                }

                is SearchEffect.Vibrate -> {
                    hapticService.vibrateIfAllowed(
                        milliseconds = effect.milliseconds,
                    )
                }

                is SearchEffect.CopyToClipboard -> {
                    val copied = myClipboardManager.copyToClipboard(
                        text = effect.text,
                    )

                    if (!copied) {
                        showToast("אין מה להעתיק")
                    }
                }

                is SearchEffect.ShareSearch -> {
                    shareLauncher.shareText(
                        text = effect.text,
                        stringResource = SharedRes.strings.search_in_bible,
                    )
                }
            }
        }
    }
}
